import React, { useState, useEffect } from 'react';
import { App, Spin, Button, Table, Typography, Space, Descriptions, Row, Col, Tag, Statistic } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { apiClient } from '../../utils/api';
import { FilePdfOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useParams, useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { StandardCard, PageTitle } from '../common';
import { theme } from '../../theme';

const { Title, Text } = Typography;

interface MuestraData {
  id: number;
  // Campos de Clasificacion_Detalle
  numero_fardo: number;
  numero_lote: number;
  calidad: string;
  calidad_numerica: number;
  origen_id: number | null;
  origen: string | null;
  cliente_id: number | null;
  cliente: string | null;
  numero_planilla?: number;
  fardo_id: number | null;
  producto_id: number | null;
  alta: number;
  // Campos de Clasificacion_hvi / clasificacion
  hvi: string;
  hebra: number;
  resistencia: number;
  micronaire: number;
  ML: number;
  AMT: number;
  CG: string;
  UI: number;
  RD: number;
  MR: number;
  ELG: number;
  B: number;
  SFF: number;
  // Alias para compatibilidad
  nro_fardo?: number;
  longitud?: number;
}

interface EmpresaInfo {
  denominacion: string;
  direccion: string;
  localidad: string;
  telefono: string;
}

interface LoteData {
  id: number | null;
  empresa_id: number;
  empresa: EmpresaInfo;
  cliente_id: number | null;
  cliente: string;
  nro_lote: number;
  cantidad_fardos: number;
  calidad_desde: string | null;
  calidad_hasta: string | null;
  micronaire_desde: number;
  micronaire_hasta: number;
  longitud_desde: number;
  longitud_hasta: number;
  resistencia_desde: number;
  resistencia_hasta: number;
  estado: number;
  ciclo_id: number;
  fecha_cierre: string | null;
  producto_id: number | null;
  muestras: MuestraData[];
  total_muestras: number;
  muestras_anuladas: number;
}

const ListadoLote: React.FC = () => {
  const { message } = App.useApp();
  const { nroLote } = useParams<{ nroLote: string }>();
  const [data, setData] = useState<LoteData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const { getAccessTokenSilently, user } = useAuth0();
  const navigate = useNavigate();

  const columns: ColumnsType<MuestraData> = [
    {
      title: 'N° Fardo',
      dataIndex: 'numero_fardo',
      key: 'numero_fardo',
      width: 100,
      align: 'center',
      fixed: 'left',
    },
    {
      title: 'Calidad',
      dataIndex: 'calidad',
      key: 'calidad',
      width: 100,
      align: 'center',
      fixed: 'left',
    },
    {
      title: 'Origen',
      dataIndex: 'origen',
      key: 'origen',
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Planilla N°',
      dataIndex: 'numero_planilla',
      key: 'numero_planilla',
      width: 110,
      align: 'right',
    },
    {
      title: 'HVI',
      dataIndex: 'hvi',
      key: 'hvi',
      width: 90,
      align: 'center',
      render: (value: string) => value || '',
    },
    {
      title: 'MIC',
      dataIndex: 'micronaire',
      key: 'micronaire',
      width: 110,
      align: 'right',
      render: (value: number) => (value !== null && value !== undefined) ? value.toFixed(2) : '0.00',
    },
    {
      title: 'UHML',
      dataIndex: 'hebra',
      key: 'hebra',
      width: 110,
      align: 'right',
      render: (value: number) => (value !== null && value !== undefined) ? value.toFixed(2) : '0.00',
    },
    {
      title: 'STR',
      dataIndex: 'resistencia',
      key: 'resistencia',
      width: 110,
      align: 'right',
      render: (value: number) => (value !== null && value !== undefined) ? value.toFixed(2) : '0.00',
    },
    {
      title: 'ML',
      dataIndex: 'ML',
      key: 'ML',
      width: 90,
      align: 'right',
      render: (value: number) => (value !== null && value !== undefined) ? value.toFixed(2) : '0.00',
    },
    {
      title: 'AMT',
      dataIndex: 'AMT',
      key: 'AMT',
      width: 90,
      align: 'right',
      render: (value: number) => (value !== null && value !== undefined) ? value.toFixed(2) : '0.00',
    },
    {
      title: 'CG',
      dataIndex: 'CG',
      key: 'CG',
      width: 90,
      align: 'center',
      render: (value: string) => value || '',
    },
    {
      title: 'UI',
      dataIndex: 'UI',
      key: 'UI',
      width: 90,
      align: 'right',
      render: (value: number) => (value !== null && value !== undefined) ? value.toFixed(2) : '0.00',
    },
    {
      title: 'RD',
      dataIndex: 'RD',
      key: 'RD',
      width: 90,
      align: 'right',
      render: (value: number) => (value !== null && value !== undefined) ? value.toFixed(2) : '0.00',
    },
    {
      title: 'MR',
      dataIndex: 'MR',
      key: 'MR',
      width: 90,
      align: 'right',
      render: (value: number) => (value !== null && value !== undefined) ? value.toFixed(2) : '0.00',
    },
    {
      title: 'ELG',
      dataIndex: 'ELG',
      key: 'ELG',
      width: 90,
      align: 'right',
      render: (value: number) => (value !== null && value !== undefined) ? value.toFixed(2) : '0.00',
    },
    {
      title: 'B',
      dataIndex: 'B',
      key: 'B',
      width: 90,
      align: 'right',
      render: (value: number) => (value !== null && value !== undefined) ? value.toFixed(2) : '0.00',
    },
    {
      title: 'SFF',
      dataIndex: 'SFF',
      key: 'SFF',
      width: 90,
      align: 'right',
      render: (value: number) => (value !== null && value !== undefined) ? value.toFixed(2) : '0.00',
    },
    {
      title: 'Estado',
      dataIndex: 'alta',
      key: 'alta',
      width: 100,
      align: 'center',
      fixed: 'right',
      render: (alta: number) => (
        <Tag color={alta === 1 ? 'green' : 'red'}>
          {alta === 1 ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
    },
  ];

  const fetchData = async () => {
    if (!user?.id_empresa || !nroLote) return;

    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      
      const response = await apiClient.get<LoteData>(
        `${process.env.REACT_APP_CLASIFICACION_URL}/listado_lotes/${user.id_empresa}/${nroLote}`,
        { token }
      );

      if (response.data) {
        // Calcular estadísticas considerando solo muestras activas
        const muestrasActivas = response.data.muestras.filter(m => m.alta === 1);
        const muestrasInactivas = response.data.muestras.filter(m => m.alta === 0);

        const processedData = {
          ...response.data,
          micronaire_desde: response.data.micronaire_desde || 0,
          micronaire_hasta: response.data.micronaire_hasta || 0,
          longitud_desde: response.data.longitud_desde || 0,
          longitud_hasta: response.data.longitud_hasta || 0,
          resistencia_desde: response.data.resistencia_desde || 0,
          resistencia_hasta: response.data.resistencia_hasta || 0,
          total_muestras: muestrasActivas.length,
          muestras_anuladas: muestrasInactivas.length
        };
        setData(processedData);
      }
    } catch (error) {
      console.error('Error al cargar los datos:', error);
      message.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [user?.id_empresa, nroLote, getAccessTokenSilently]);

  const fmt = (val: number | null | undefined): string =>
    (val === null || val === undefined) ? '0.00' : Number(val).toFixed(2);

  const drawLoteHeader = (doc: jsPDF): number => {
    const pw = doc.internal.pageSize.getWidth();
    const m = 10;
    // Barra oscura
    const barH = 14;
    doc.setFillColor(0, 58, 140);
    doc.rect(m, m, pw - 2 * m, barH, 'F');
    // Nombre empresa
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(data!.empresa.denominacion, m + 4, m + 7);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`${data!.empresa.direccion} — ${data!.empresa.localidad}`, m + 4, m + 12);
    // Badge lote
    const badgeW = 36;
    const badgeX = pw - m - badgeW;
    doc.setFillColor(255, 255, 255);
    doc.rect(badgeX, m + 2, badgeW, barH - 4, 'F');
    doc.setTextColor(0, 58, 140);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`LOTE N° ${data!.nro_lote}`, badgeX + badgeW / 2, m + 10, { align: 'center' });
    // Grid datos del lote (4 cols x 2 filas)
    const gridY = m + barH + 3;
    const cellW = (pw - 2 * m) / 4;
    const cellH = 11;
    const cells = [
      { label: 'Cliente', value: data!.cliente || '—' },
      { label: 'Cantidad fardos', value: String(data!.cantidad_fardos) },
      { label: 'Estado', value: data!.estado === 2 ? 'CERRADO' : 'ABIERTO', isEstado: true },
      { label: 'Activas / Anuladas', value: `${data!.total_muestras} / ${data!.muestras_anuladas}` },
      { label: 'Calidad', value: data!.calidad_desde ? `${data!.calidad_desde} — ${data!.calidad_hasta}` : '—' },
      { label: 'MIC', value: `${fmt(data!.micronaire_desde)} — ${fmt(data!.micronaire_hasta)}` },
      { label: 'UHML', value: `${fmt(data!.longitud_desde)} — ${fmt(data!.longitud_hasta)}` },
      { label: 'STR', value: `${fmt(data!.resistencia_desde)} — ${fmt(data!.resistencia_hasta)}` },
    ];
    cells.forEach((cell, i) => {
      const col = i % 4;
      const row = Math.floor(i / 4);
      const x = m + col * cellW;
      const y = gridY + row * cellH;
      doc.setFillColor(250, 250, 250);
      doc.setDrawColor(217, 217, 217);
      doc.setLineWidth(0.2);
      doc.rect(x, y, cellW, cellH, 'FD');
      doc.setFontSize(6);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(136, 136, 136);
      doc.text(cell.label.toUpperCase(), x + 3, y + 4);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      if (cell.isEstado) {
        doc.setTextColor(data!.estado === 2 ? 207 : 56, data!.estado === 2 ? 19 : 158, data!.estado === 2 ? 34 : 13);
      } else {
        doc.setTextColor(34, 34, 34);
      }
      doc.text(cell.value, x + 3, y + 9);
    });
    return gridY + 2 * cellH + 5;
  };

  const addLoteResumen = (doc: jsPDF, cols: { key: keyof MuestraData; label: string }[], startY: number) => {
    const rows = cols.map(col => {
      const vals = data!.muestras
        .filter(m => m.alta === 1)
        .map(m => Number(m[col.key] || 0))
        .filter(v => v !== 0);
      if (!vals.length) return null;
      return [col.label, Math.min(...vals).toFixed(2), Math.max(...vals).toFixed(2),
        (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2)];
    }).filter(Boolean) as string[][];
    if (!rows.length) return;
    const pw = doc.internal.pageSize.getWidth();
    doc.setFillColor(24, 144, 255);
    doc.rect(10, startY, pw - 20, 7, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('Resumen Estadístico', 13, startY + 5);
    autoTable(doc, {
      head: [['Campo', 'Mínimo', 'Máximo', 'Promedio']],
      body: rows,
      startY: startY + 8,
      tableWidth: (pw - 20) * 0.55,
      margin: { left: 10, right: 10 },
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [250, 250, 250] },
      columnStyles: { 0: { fontStyle: 'bold' }, 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right', fontStyle: 'bold' } },
    });
  };

  const handleGeneratePdfReducido = async () => {
    if (!data) return;
    message.loading({ content: 'Generando PDF...', key: 'pdfGeneration' });
    try {
      const KEY_COLS: { key: keyof MuestraData; label: string }[] = [
        { key: 'hebra', label: 'UHML' }, { key: 'resistencia', label: 'STR' },
        { key: 'micronaire', label: 'MIC' }, { key: 'UI', label: 'UI' },
        { key: 'RD', label: 'RD' }, { key: 'ELG', label: 'ELG' },
      ];
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const startY = drawLoteHeader(doc);
      const pw = doc.internal.pageSize.getWidth();
      doc.setFillColor(24, 144, 255);
      doc.rect(10, startY, pw - 20, 7, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text('Muestras del Lote', 13, startY + 5);
      autoTable(doc, {
        head: [['N° Fardo', 'Calidad', 'Origen', 'Planilla', 'HVI', 'UHML', 'STR', 'MIC', 'UI', 'CG', 'RD', 'ELG', 'Estado']],
        body: data.muestras.map(m => [
          String(m.numero_fardo), m.calidad, m.origen || '—',
          String(m.numero_planilla ?? ''), m.hvi || '',
          fmt(m.hebra), fmt(m.resistencia), fmt(m.micronaire),
          fmt(m.UI), m.CG || '', fmt(m.RD), fmt(m.ELG),
          m.alta === 1 ? 'Activo' : 'Anulado',
        ]),
        startY: startY + 8,
        margin: { left: 10, right: 10 },
        styles: { fontSize: 9, cellPadding: 3, halign: 'right' },
        headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'center' },
        alternateRowStyles: { fillColor: [250, 250, 250] },
        rowPageBreak: 'avoid',
        columnStyles: {
          0: { halign: 'center' }, 1: { halign: 'center' }, 2: { halign: 'left' },
          3: { halign: 'center' }, 4: { halign: 'center' }, 9: { halign: 'center' },
          12: { halign: 'center' },
        },
        didParseCell: (hookData: any) => {
          if (hookData.section === 'body' && data.muestras[hookData.row.index]?.alta === 0) {
            hookData.cell.styles.fillColor = [255, 235, 238];
            hookData.cell.styles.textColor = [150, 150, 150];
          }
        },
      });
      addLoteResumen(doc, KEY_COLS, (doc as any).lastAutoTable.finalY + 8);
      doc.save(`lote_reducido_${data.nro_lote}.pdf`);
      message.success({ content: 'PDF generado exitosamente', key: 'pdfGeneration', duration: 2 });
    } catch (err) {
      console.error('Error al generar PDF:', err);
      message.error({ content: 'Error al generar el PDF', key: 'pdfGeneration', duration: 2 });
    }
  };

  const handleGeneratePdfCompleto = async () => {
    if (!data) return;
    message.loading({ content: 'Generando PDF...', key: 'pdfGeneration' });
    try {
      const ALL_COLS: { key: keyof MuestraData; label: string }[] = [
        { key: 'hebra', label: 'UHML' }, { key: 'resistencia', label: 'STR' },
        { key: 'micronaire', label: 'MIC' }, { key: 'ML', label: 'ML' },
        { key: 'AMT', label: 'AMT' }, { key: 'UI', label: 'UI' },
        { key: 'RD', label: 'RD' }, { key: 'MR', label: 'MR' },
        { key: 'ELG', label: 'ELG' }, { key: 'B', label: 'B' }, { key: 'SFF', label: 'SFF' },
      ];
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pw = doc.internal.pageSize.getWidth(); // 297mm
      const m = 10;
      // Header: bloque empresa izquierda + grid derecha
      const hdrH = 28;
      const empresaW = 60;
      doc.setFillColor(29, 53, 87);
      doc.rect(m, m, empresaW, hdrH, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(data.empresa.denominacion, m + 4, m + 9);
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'normal');
      doc.text(data.empresa.direccion, m + 4, m + 15);
      doc.text(data.empresa.localidad, m + 4, m + 20);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(144, 205, 244);
      doc.text('INFORME DE LOTE', m + 4, m + 26);
      // Grid derecho
      const gridX = m + empresaW;
      const gridW = pw - 2 * m - empresaW;
      const cellCount = 5;
      const cellW = gridW / cellCount;
      const cellH = hdrH / 2;
      const gridCells = [
        { label: 'N° Lote', value: String(data.nro_lote) },
        { label: 'Cliente', value: data.cliente || '—' },
        { label: 'Cant. Fardos', value: String(data.cantidad_fardos) },
        { label: 'Estado', value: data.estado === 2 ? 'CERRADO' : 'ABIERTO', isEstado: true },
        { label: 'Activas / Anuladas', value: `${data.total_muestras} / ${data.muestras_anuladas}` },
        { label: 'Calidad', value: data.calidad_desde ? `${data.calidad_desde} — ${data.calidad_hasta}` : '—' },
        { label: 'MIC', value: `${fmt(data.micronaire_desde)} — ${fmt(data.micronaire_hasta)}` },
        { label: 'UHML', value: `${fmt(data.longitud_desde)} — ${fmt(data.longitud_hasta)}` },
        { label: 'STR', value: `${fmt(data.resistencia_desde)} — ${fmt(data.resistencia_hasta)}` },
        { label: 'Fecha cierre', value: data.fecha_cierre ? new Date(data.fecha_cierre).toLocaleDateString() : '—' },
      ];
      gridCells.forEach((cell, i) => {
        const col = i % cellCount;
        const row = Math.floor(i / cellCount);
        const x = gridX + col * cellW;
        const y = m + row * cellH;
        doc.setFillColor(row === 0 ? 248 : 240, row === 0 ? 249 : 242, row === 0 ? 250 : 248);
        doc.setDrawColor(209, 213, 219);
        doc.setLineWidth(0.2);
        doc.rect(x, y, cellW, cellH, 'FD');
        doc.setFontSize(5.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(107, 114, 128);
        doc.text(cell.label.toUpperCase(), x + 2.5, y + 4);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        if (cell.isEstado) {
          doc.setTextColor(data.estado === 2 ? 207 : 56, data.estado === 2 ? 19 : 158, data.estado === 2 ? 34 : 13);
        } else {
          doc.setTextColor(29, 53, 87);
        }
        doc.text(cell.value, x + 2.5, y + 10);
      });
      const tableStartY = m + hdrH + 5;
      doc.setFillColor(24, 144, 255);
      doc.rect(m, tableStartY, pw - 2 * m, 7, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text('Detalle de Muestras', m + 3, tableStartY + 5);
      autoTable(doc, {
        head: [['N° Fardo', 'Cal.', 'Origen', 'Planilla', 'HVI',
          'UHML', 'STR', 'MIC', 'ML', 'AMT', 'CG', 'UI', 'RD', 'MR', 'ELG', 'B', 'SFF', 'Estado']],
        body: data.muestras.map(m2 => [
          String(m2.numero_fardo), m2.calidad, m2.origen || '—',
          String(m2.numero_planilla ?? ''), m2.hvi || '',
          fmt(m2.hebra), fmt(m2.resistencia), fmt(m2.micronaire),
          fmt(m2.ML), fmt(m2.AMT), m2.CG || '',
          fmt(m2.UI), fmt(m2.RD), fmt(m2.MR), fmt(m2.ELG), fmt(m2.B), fmt(m2.SFF),
          m2.alta === 1 ? 'Activo' : 'Anulado',
        ]),
        startY: tableStartY + 8,
        margin: { left: 10, right: 10 },
        styles: { fontSize: 7, cellPadding: 2, halign: 'right' },
        headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'center' },
        alternateRowStyles: { fillColor: [250, 250, 250] },
        rowPageBreak: 'avoid',
        columnStyles: {
          0: { halign: 'center', cellWidth: 12 }, 1: { halign: 'center', cellWidth: 10 },
          2: { halign: 'left', cellWidth: 20 }, 3: { halign: 'center', cellWidth: 14 },
          4: { halign: 'center', cellWidth: 14 }, 10: { halign: 'center' }, 17: { halign: 'center' },
        },
        didParseCell: (hookData: any) => {
          if (hookData.section === 'body' && data.muestras[hookData.row.index]?.alta === 0) {
            hookData.cell.styles.fillColor = [255, 235, 238];
            hookData.cell.styles.textColor = [150, 150, 150];
          }
        },
      });
      addLoteResumen(doc, ALL_COLS, (doc as any).lastAutoTable.finalY + 8);
      doc.save(`lote_completo_${data.nro_lote}.pdf`);
      message.success({ content: 'PDF generado exitosamente', key: 'pdfGeneration', duration: 2 });
    } catch (err) {
      console.error('Error al generar PDF:', err);
      message.error({ content: 'Error al generar el PDF', key: 'pdfGeneration', duration: 2 });
    }
  };


  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Text>No se encontraron datos para el lote seleccionado</Text>
      </div>
    );
  }

  return (
    <div className="print-content" style={{ padding: '0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/emision_lotes')}>
          Volver
        </Button>
        <Space>
          <Button
            icon={<FilePdfOutlined />}
            onClick={handleGeneratePdfReducido}
            style={{ backgroundColor: theme.colors.primary, borderColor: theme.colors.primary, color: '#fff', fontWeight: 500 }}
          >
            PDF Reducido
          </Button>
          <Button
            icon={<FilePdfOutlined />}
            onClick={handleGeneratePdfCompleto}
            style={{ backgroundColor: theme.colors.primary, borderColor: theme.colors.primary, color: '#fff', fontWeight: 500 }}
          >
            PDF Completo
          </Button>
        </Space>
      </div>
      
      <StandardCard
        title={
          <div style={{ 
            textAlign: 'center', 
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <div style={{ fontSize: '1.1rem', fontWeight: 600, color: 'white', textAlign: 'center' }}>
                SERVI SOFT S.R.L.
              </div>
              <div style={{ fontSize: '0.9rem', fontWeight: 400, color: 'white', textAlign: 'center' }}>
                Ruta Nacional N° 8 Km 306 - Villa Mercedes - San Luis
              </div>
            </Space>
          </div>
        }
      >
        <Descriptions 
          title={
            <div style={{ 
              backgroundColor: theme.colors.primaryDark, 
              color: 'white', 
              padding: '8px 16px',
              borderRadius: theme.styles.borderRadius,
              marginBottom: '16px',
              textAlign: 'center',
              width: '100%'
            }}>
              <Title level={4} style={{ color: 'white', margin: 0 }}>Datos del Lote</Title>
            </div>
          } 
          column={{ xxl: 3, xl: 3, lg: 2, md: 2, sm: 1, xs: 1 }}
        >
            <Descriptions.Item label="Cliente">{data.cliente}</Descriptions.Item>
            <Descriptions.Item label="N° Lote">{data.nro_lote}</Descriptions.Item>
            <Descriptions.Item label="Cantidad de Fardos">{data.cantidad_fardos}</Descriptions.Item>
            <Descriptions.Item label="Estado">
              <Tag color={data.estado === 2 ? 'red' : 'green'}>
                {data.estado === 2 ? 'Cerrado' : 'Abierto'}
              </Tag>
            </Descriptions.Item>
            {data.fecha_cierre && (
              <Descriptions.Item label="Fecha de Cierre">
                {new Date(data.fecha_cierre).toLocaleDateString()}
              </Descriptions.Item>
            )}
        </Descriptions>

        <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
          <Col xs={24} sm={12} md={8}>
            <StandardCard>
              <Statistic
                title="Total de Muestras Activas"
                value={data.total_muestras}
                valueStyle={{ color: theme.colors.primary }}
              />
            </StandardCard>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <StandardCard>
              <Statistic
                title="Muestras Activas"
                value={data.total_muestras}
                valueStyle={{ color: theme.colors.success }}
              />
            </StandardCard>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <StandardCard>
              <Statistic
                title="Muestras Inactivas"
                value={data.muestras_anuladas}
                valueStyle={{ color: theme.colors.error }}
              />
            </StandardCard>
          </Col>
        </Row>

        <StandardCard 
          title="Características de las Muestras"
          style={{ marginTop: '24px' }}
        >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <StandardCard 
                  title="Calidad"
                >
                  <Text strong>Rango: {data.calidad_desde || '-'} - {data.calidad_hasta || '-'}</Text>
                </StandardCard>
              </Col>
              <Col xs={24} sm={12}>
                <StandardCard 
                  title="MIC"
                >
                  <Text strong>Rango: {data.micronaire_desde?.toFixed(2) || '0.00'} - {data.micronaire_hasta?.toFixed(2) || '0.00'}</Text>
                </StandardCard>
              </Col>
              <Col xs={24} sm={12}>
                <StandardCard 
                  title="UHML"
                >
                  <Text strong>Rango: {data.longitud_desde?.toFixed(2) || '0.00'} - {data.longitud_hasta?.toFixed(2) || '0.00'}</Text>
                </StandardCard>
              </Col>
              <Col xs={24} sm={12}>
                <StandardCard 
                  title="STR"
                >
                  <Text strong>Rango: {data.resistencia_desde?.toFixed(2) || '0.00'} - {data.resistencia_hasta?.toFixed(2) || '0.00'}</Text>
                </StandardCard>
              </Col>
            </Row>
        </StandardCard>

        <StandardCard 
          title="Muestras del Lote"
          bodyStyle={{ padding: 0 }}
          style={{ marginTop: '24px' }}
        >
          <Table
            columns={columns}
            dataSource={data.muestras}
            className="listado-lote-table"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} muestras`,
            }}
            size="middle"
            rowKey="id"
            scroll={{ x: 'max-content' }}
          />
        </StandardCard>
      </StandardCard>

      <style>
        {`
          .listado-lote-table .ant-table-thead > tr > th {
            white-space: nowrap;
            text-align: center;
            vertical-align: middle;
          }
          .listado-lote-table .ant-table-tbody > tr > td {
            white-space: nowrap;
            vertical-align: middle;
          }
        `}
      </style>
    </div>
  );
};

export default ListadoLote;
